import React from 'react';
import bash from 'react-syntax-highlighter/dist/languages/bash';
import js from 'react-syntax-highlighter/dist/languages/javascript';
import json from 'react-syntax-highlighter/dist/languages/json';
import python from 'react-syntax-highlighter/dist/languages/python';
import r from 'react-syntax-highlighter/dist/languages/r';
import yaml from 'react-syntax-highlighter/dist/languages/yaml';
import ReactSyntaxHighlighter, { registerLanguage } from 'react-syntax-highlighter/dist/light';
import darkStyle from 'react-syntax-highlighter/dist/styles/agate';
import lightStyle from 'react-syntax-highlighter/dist/styles/xcode';

import { THEME_ALIASES } from '@neptune/shared/venus-domain';
import { bemBlock, Layout, useTheme } from '@neptune/shared/venus-ui';

import { ArrayElementType } from 'common/utility-types';

import { CopyToClipboard } from '../copy-to-clipboard/CopyToClipboard';

import './SyntaxHighlighter.less';

registerLanguage('bash', bash);
registerLanguage('json', json);
registerLanguage('python', python);
registerLanguage('yaml', yaml);
registerLanguage('js', js);
registerLanguage('r', r);

const SUPPORTED_LANGUAGES = ['json', 'python', 'bash', 'yaml'] as const;
const SUPPORTED_THEMES = {
  [THEME_ALIASES.DARK]: darkStyle,
  [THEME_ALIASES.LIGHT]: lightStyle,
} as const;

const HIGHLIGHTED_LINE_STYLE = {
  backgroundColor: 'rgba(19, 138, 174, 0.15)',
  display: 'block',
} as const;

export type SyntaxHighlighterLanguage = ArrayElementType<typeof SUPPORTED_LANGUAGES>;

type SyntaxHighlighterProps = {
  codeToCopy?: string;
  className?: string;
  codeToHighlight?: string;
  copyButtonTitle?: string;
  copyDisabled?: boolean;
  language?: SyntaxHighlighterLanguage;
  hlLines?: number[];
  wrapLines?: boolean;
};

const block = bemBlock('syntax-highlighter');

export const SyntaxHighlighter = ({
  codeToCopy = '',
  className,
  codeToHighlight = '',
  copyButtonTitle,
  copyDisabled = false,
  language,
  hlLines,
  wrapLines = true,
  ...highlighterProps
}: SyntaxHighlighterProps) => {
  const { theme } = useTheme();

  const hlLinesSet = new Set(hlLines);
  const validLanguage = language && SUPPORTED_LANGUAGES.includes(language) ? language : null;
  const highlightStyle = SUPPORTED_THEMES[theme];

  return (
    <Layout.Column
      className={block({
        modifiers: { wrapLines },
        extra: className,
      })}
      overflow="hidden"
      span="auto"
    >
      <Layout.Row className={block('buttons')} alignItems="center">
        {!copyDisabled && (
          <CopyToClipboard
            data-heap-redact-text
            className={block('button')}
            text={codeToCopy || codeToHighlight}
            title={copyButtonTitle}
            variant="tertiary"
          />
        )}
      </Layout.Row>
      <ReactSyntaxHighlighter
        className={block('code')}
        language={validLanguage}
        style={highlightStyle}
        wrapLines
        lineStyle={(lineNo: number) => {
          if (hlLinesSet.has(lineNo)) {
            return HIGHLIGHTED_LINE_STYLE;
          }

          return null;
        }}
        data-role="syntax-highlighter"
        data-heap-redact-text
        {...highlighterProps}
      >
        {codeToHighlight}
      </ReactSyntaxHighlighter>
    </Layout.Column>
  );
};
